import withStyles from "react-jss"
import colors from "../../assets/colors"
import {
  MAX_WIDTH,
  SCROLLY_TEXTBOX_OUTER_PADDING,
} from "../../data/globalStyles"
import { RiskChart } from "../../assets/images"
import { Link } from ".."

const TEXT_BOTTOM_PADDING = "30px"

const styles = {
  container: {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: MAX_WIDTH,
    margin: "0 auto",
    color: colors.black,
    ...SCROLLY_TEXTBOX_OUTER_PADDING,
  },
  textContainer: {
    boxSizing: "border-box",
    width: "100%",
    background: "#ffffff",
    borderRadius: "4px",
    padding: "32px 40px",
    "@media only screen and (max-width: 840px)": {
      padding: "16px",
    },
    "& > :last-child": {
      paddingBottom: 0,
    },
  },
  text: {
    boxSizing: "border-box",
    fontFamily: "BreveText",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "19px",
    margin: 0,
    paddingBottom: TEXT_BOTTOM_PADDING,
    color: "#000000",
    lineHeight: "1.5em",
  },
  figure: {
    margin: "0 auto",
    width: "100%",
    maxWidth: "600px",
    marginBottom: TEXT_BOTTOM_PADDING,
  },
  img: {
    width: "100%",
  },
  caption: {
    width: "100%",
    textAlign: "center",
    fontFamily: "WiredMono",
    fontWeight: 400,
    fontSize: "13px",
    letterSpacing: "-0.02em",
    textTransform: "uppercase",
    margin: "7px 0",
  },
  quote: {
    boxSizing: "border-box",
    margin: 0,
    marginBottom: TEXT_BOTTOM_PADDING,
    fontFamily: "BreveText",
    fontWeight: 400,
    fontSize: "32px",
    lineHeight: "36px",
    letterSpacing: "-0.02em",
    borderWidth: "0px 0px 0px 4px",
    borderStyle: "solid",
    borderColor: "rgb(26, 26, 26)",
    padding: "0px 1.5rem",
    "@media (max-width: 480px)": {
      padding: "0px 1rem",
    },
  },
  leadInTextCallout: {
    fontFamily: "Apercu, helvetica, sans-serif",
    fontSize: "18px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "1.5px",
  },
  callout: {
    boxSizing: "border-box",
    width: "100%",
    margin: "0 2.5rem 1rem 0px",
    padding: 0,
    "@media (min-width: 1024px)": {
      minWidth: "220px",
    },
    "@media (min-width: 768px)": {
      width: "37.5%",
      margin: "0.5rem 2rem 0.5rem 0px",
      minWidth: "270px",
      float: "left",
    },
  },
  aside: {
    padding: "1rem 0 0 0",
    margin: 0,
    borderTop: "1px solid #1a1a1a",
    borderBottom: "1px solid #e5e5e5",
    "& p": {
      fontFamily: "BreveText, helvetica, sans-serif",
      fontStyle: "normal",
      fontSize: "14px",
      fontWeight: 400,
      letterSpacing: "0.006em",
      overflowWrap: "normal",
      lineBreak: "auto",
      lineHeight: "1.57em",
      color: "#1a1a1a",
      marginTop: 0,
    },
  },
  dropcap: {
    width: "100%",
    height: "auto",
  },
}

const IntroTextSection = ({ classes }) => {
  return (
    <section className={classes.container}>
      <div className={classes.textContainer}>
        <p className={classes.text}>
          <span className={classes.leadInTextCallout}>Every year, the</span>{" "}
          city of Rotterdam in the Netherlands gives some 30,000 people welfare
          benefits to help them make rent, buy food, and pay essential bills.
          And every year, thousands of those people are investigated under
          suspicion of committing benefits fraud. But in recent years, the way
          that people have been flagged as suspicious has changed.
        </p>
        <p className={classes.text}>
          In 2017, the city deployed a machine learning algorithm built by
          consulting firm Accenture. The algorithm, which generates a risk score
          for everyone on welfare, was trained to catch lawbreakers using data
          about individuals who had been investigated for fraud in the past.
          This risk score is dictated by attributes such as age, gender, and
          Dutch language ability. And rather than use this data to work out how
          much welfare aid people should receive, the city used it to work out
          who should be investigated for fraud.
        </p>
        <div className={classes.callout}>
          <aside className={classes.aside}>
            <p>
              This story is part of a joint investigation between{" "}
              <Link href="https://www.lighthousereports.nl/">
                Lighthouse Reports
              </Link>{" "}
              and WIRED. To read other stories from the series,{" "}
              <Link href="https://www.wired.com/tag/series-suspicion-machine/">
                click here
              </Link>
              .
            </p>
          </aside>
        </div>
        <p className={classes.text}>
          When the Rotterdam system was deployed, Accenture hailed its
          "sophisticated data-driven approach" as an example to other cities.
          Rotterdam took over development of the algorithm in 2018. But in 2021,
          the city suspended use of the system after it received a critical
          external ethical review commissioned by the Dutch government, although
          Rotterdam continues to develop an alternative.
        </p>

        <blockquote className={classes.quote}>
          By reconstructing the system and testing how it works, we found that
          it discriminates based on ethnicity and gender.
        </blockquote>
        <p className={classes.text}>
          Lighthouse Reports and WIRED obtained Rotterdam’s welfare fraud
          algorithm and the data used to train it, giving unprecedented insight
          into how such systems work. This level of access, negotiated under
          freedom-of-information laws, enabled us to examine the personal data
          fed into the algorithm, the inner workings of the data processing, and
          the scores it generates. By{" "}
          <Link href="https://www.lighthousereports.com/suspicion-machines-methodology/">
            reconstructing the system and testing how it works
          </Link>
          , we found that it discriminates based on ethnicity and gender. It
          also revealed evidence of fundamental flaws that made the system both
          inaccurate and unfair.
        </p>

        <p className={classes.text}>
          Rotterdam’s algorithm is best thought of as a suspicion machine. It
          judges people on many characteristics they cannot control (like gender
          and ethnicity). What might appear to a caseworker to be a
          vulnerability, such as a person showing signs of low self-esteem, is
          treated by the machine as grounds for suspicion when the caseworker
          enters a comment into the system. The data fed into the algorithm
          ranges from invasive (the length of someone’s last romantic
          relationship) and subjective (someone’s ability to convince and
          influence others) to banal (how many times someone has emailed the
          city) and seemingly irrelevant (whether someone plays sports). Despite
          the scale of data used to calculate risk scores, it performs little
          better than random selection.{" "}
        </p>
        <p className={classes.text}>
          Machine learning algorithms like Rotterdam’s are being used to make
          more and more decisions about people’s lives, including what{" "}
          <Link href="https://www.theguardian.com/education/2021/feb/18/the-student-and-the-algorithm-how-the-exam-results-fiasco-threatened-one-pupils-future">
            schools their children attend
          </Link>
          ,{" "}
          <Link href="https://hbr.org/2019/05/all-the-ways-hiring-algorithms-can-introduce-bias">
            who gets interviewed for jobs
          </Link>
          , and{" "}
          <Link href="https://themarkup.org/denied/2021/08/25/the-secret-bias-hidden-in-mortgage-approval-algorithms">
            which family gets a loan
          </Link>
          . Millions of people are being scored and ranked as they go about
          their daily lives, with profound implications. The spread of
          risk-scoring models is presented as progress, promising mathematical
          objectivity and fairness. Yet citizens have no real way to understand
          or question the decisions such systems make.
        </p>
        <p className={classes.text}>
          Governments typically refuse to provide any technical details to back
          up claims of accuracy and neutrality. In the rare cases where
          watchdogs have overcame official stonewalling, they've found the
          systems to be anything but unbiased. Reports have found discriminatory
          patterns in{" "}
          <Link href="https://hai.stanford.edu/news/how-flawed-data-aggravates-inequality-credit">
            credit scoring
          </Link>
          ,{" "}
          <Link href="https://www.propublica.org/article/machine-bias-risk-assessments-in-criminal-sentencing">
            criminal justice
          </Link>
          , and{" "}
          <Link href="https://www.reuters.com/article/us-amazon-com-jobs-automation-insight/amazon-scraps-secret-ai-recruiting-tool-that-showed-bias-against-women-idUSKCN1MK08G">
            hiring practices
          </Link>
          , among others.{" "}
        </p>
        <p className={classes.text}>
          Being flagged for investigation can ruin someone’s life, and the
          opacity of the system makes it nearly impossible to challenge being
          selected for an investigation, let alone stop one that’s already
          underway. One mother put under investigation in Rotterdam{" "}
          <Link href="https://www.ombudsmanrotterdam.nl/web/uploads/2021/06/210526-Rapport-8288-Ws.pdf">
            faced
          </Link>{" "}
          a raid from fraud controllers who rifled through her laundry, counted
          toothbrushes, and asked intimate questions about her life in front of
          her children. Her complaint against investigators was later
          substantiated by an official review.{" "}
        </p>
        <blockquote className={classes.quote}>
          The opacity of the system makes it nearly impossible to challenge
          being selected for an investigation.
        </blockquote>
        <p className={classes.text}>
          But how did the algorithm decide who to investigate? And how did it
          make those decisions? To work this out, we created two hypothetical
          people, or archetypes: “Sara,” a single mother, and “Yusef,” who was
          born outside the Netherlands. Their characteristics and the way they
          interact with Rotterdam’s algorithm show how it discriminated when
          scoring real people.{" "}
        </p>
        <figure className={classes.figure}>
          <img
            className={classes.img}
            src={RiskChart}
            alt="A bar chart showing that non-parents, those over 60 and men have a decreased risk of being flagged for fraud while parents, those in their 30s and women have an increased risk."
          />
        </figure>
      </div>
    </section>
  )
}

export default withStyles(styles)(IntroTextSection)
